/* eslint-disable jsx-a11y/no-autofocus */
// BJR: Need to disable no autofocus rule because we need
// to autofocus inputs in order for HoneyWell Scanner to work correctly
import * as React from 'react';
import { debounce } from 'lodash';
import Alert from '@material-ui/lab/Alert';
import {
  createStyles, Grid, makeStyles,
  Box, TextField, Divider, CircularProgress,
} from '@material-ui/core';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import useSound from 'use-sound';
import CurrentBox from '../receiving/CurrentBox';
import CurrentBatch from './CurrentBatch';
import BoxesScanned from './BoxesScanned';
import type IBox from '../receiving/ReceivingInterfaces';
import MarkBoxDamagedDialog from './MarkBoxDamagedDialog';
import DuplicateTrackingNumberDialog from './DuplicateTrackingNumberDialog';
import SpecialCaseBoxes from './SpecialCaseBoxes';
import useEventListener from '../../lib/useEventListener';
import errorSound from '../../assets/sounds/error.wav';
import successSound from '../../assets/sounds/success.wav';
import warningSound from '../../assets/sounds/warning.wav';
import prioritySound from '../../assets/sounds/priority.wav';

const useStyles = makeStyles(() => createStyles({
  root: {
    flexGrow: 1,
  },
  openerCard: {
    height: 180,
  },
  openerSvg: {
    height: 24,
    width: 24,
    marginBottom: 7,
  },
  noBorder: {
    border: 'none',
  },
}));

interface IntakeVm {
  boxes: Array<IBox>
}

const Intake: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [playError] = useSound(errorSound);
  const [playWarning] = useSound(warningSound);
  const [playSuccess] = useSound(successSound);
  const [playPriority] = useSound(prioritySound);

  const boxInputRef = React.useRef<HTMLInputElement>(null);

  const [lastScannedId, setLastScannedId] = React.useState<string>();
  const [addBoxProcessing, setAddBoxProcessing] = React.useState<boolean>(false);
  const [trackingId, setTrackingId] = React.useState<string>();
  const [firstBoxScanTime, setFirstBoxScanTime] = React.useState<string>();

  const [markDamagedDialogState, setMarkDamagedDialogState] = React.useState({
    open: false,
    markDamagedId: '',
  });

  const [DuplicateTrackingNumberDialogState, setDuplicateTrackingNumberDialogState] = React.useState({
    open: false,
  });

  const openMarkBoxDamaged = (boxId: string) => {
    setMarkDamagedDialogState({ ...markDamagedDialogState, markDamagedId: boxId, open: true });
  };

  const fetcher = (url: string, scanTimeParam: string) => axios.get<IntakeVm>(url, {
    params: {
      scanTime: scanTimeParam,
    },
  }).then((res) => res.data);

  const { data, mutate } = useSWR<IntakeVm>(firstBoxScanTime ? ['/Intake/GetBatch', firstBoxScanTime] : null, (url, scanTimeParam) => fetcher(url, scanTimeParam));

  const markBoxDamaged = (boxId: string, description: string, isReject: boolean): void => {
    axios.post<string>('/Intake/MarkBoxDamaged', { boxId, description, isReject })
      .then(() => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
      })
      .catch((error: AxiosError): void => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  const addBox = debounce((trackingNumber: string, override: boolean | undefined = false): void => {
    if (trackingNumber !== '') {
      setAddBoxProcessing(true);
      boxInputRef.current?.setRangeText('', 0, boxInputRef.current.value.length);
      if (lastScannedId === trackingNumber && override === false) {
        setAddBoxProcessing(false);
        playError();
        enqueueSnackbar(`Tracking number ${trackingNumber} has already scanned`, { autoHideDuration: 5000, variant: 'warning' });
      } else {
        setLastScannedId(trackingNumber);
        axios.post<{ isDuplicate: boolean, Id: string, scanTime: string, highPriority: boolean }>('/Intake/AddBox', { trackingNumber, OverrideDuplicate: override })
          .then((response) => {
            setTrackingId(trackingNumber);
            setDuplicateTrackingNumberDialogState({ ...DuplicateTrackingNumberDialogState, open: response.data.isDuplicate });
            if (!response.data.isDuplicate) {
              if (!firstBoxScanTime) {
                setFirstBoxScanTime(response.data.scanTime);
                setAddBoxProcessing(false);
              }
              if (response.data.highPriority) {
                playPriority();
                setAddBoxProcessing(false);
              } else {
                playSuccess();
                setAddBoxProcessing(false);
              }
              enqueueSnackbar(`${trackingNumber} scanned in successfully`, { autoHideDuration: 2500, variant: 'success' });
            } else {
              playWarning();
              setAddBoxProcessing(false);
            }
            mutate().catch((error: AxiosError): void => {
              enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
            });
          })
          .catch((error: AxiosError): void => {
            playError();
            setAddBoxProcessing(false);
            enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
          });
      }
    }
  }, 100);

  const handleMarkDamaged = (description: string, accept: boolean) => {
    markBoxDamaged(markDamagedDialogState.markDamagedId, description, accept);
  };

  const handleDamagedClose = () => {
    setMarkDamagedDialogState({ ...markDamagedDialogState, open: false, markDamagedId: '' });
  };

  const handleDuplicateOverride = () => {
    if (trackingId) {
      addBox(trackingId, true);
      setDuplicateTrackingNumberDialogState({ ...DuplicateTrackingNumberDialogState, open: false });
      boxInputRef.current?.focus();
    }
  };

  const handleDuplicateClose = () => {
    setDuplicateTrackingNumberDialogState({ ...DuplicateTrackingNumberDialogState, open: false });
    boxInputRef.current?.focus();
  };

  const addBoxInTable = (trackingNumber: string, override = false) => new Promise<{ isDuplicate: boolean, Id: string }>((resolve, reject) => {
    axios.post<{ isDuplicate: boolean, Id: string }>('/Intake/AddBox', { trackingNumber, OverrideDuplicate: override })
      .then((response) => {
        setTrackingId(trackingNumber);
        setDuplicateTrackingNumberDialogState({ ...DuplicateTrackingNumberDialogState, open: response?.data?.isDuplicate });
        mutate().catch((error: AxiosError): void => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        });
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        enqueueSnackbar(`The tracking number '${trackingNumber}' had the following error error: ${String(error?.response?.data)}`, { persist: false, variant: 'error' });
        reject(error?.response?.data);
      });
  });

  const deleteBox = (boxId: string): void => {
    axios.post<string>('/Intake/DeleteBox', { boxId })
      .then(() => {
        mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
      })
      .catch((error: AxiosError): void => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
  };

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key?.length === 1 || e.key === 'Enter') {
      if (e.key !== 'Enter') {
        barcodeBuffer = `${barcodeBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented) {
          addBox(barcodeBuffer);
        }
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  if (!data || data.boxes.length === 0) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'To get started, scan in a box.'}</Alert>
        </Grid>
        <Grid item>
          {addBoxProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                id="intake-scan-input"
                variant="outlined"
                size="small"
                onKeyPress={
              (event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                  addBox((event.target as HTMLInputElement).value);
                }
              }
            }
              />
            )}
        </Grid>
        <DuplicateTrackingNumberDialog
          showModal={DuplicateTrackingNumberDialogState.open}
          onClose={handleDuplicateClose}
          markOverride={handleDuplicateOverride}
          trackingNumber={trackingId}
        />
      </Grid>
    );
  }

  const lastBoxScanned = data.boxes.reduce((prev, current) => ((prev.createdAt > current.createdAt) ? prev : current));
  const damagedBox = data.boxes.find((b) => b.id === markDamagedDialogState.markDamagedId);

  return (
    <>
      <Box className={classes.root}>
        <Grid container spacing={0}>
          <Grid container item spacing={1} xs={9}>
            <Grid item xs={1} />
            <Grid container item spacing={2} xs={11}>
              <Grid item xs={12}>
                <Alert
                  severity="info"
                >
                  {'Continue scanning boxes in until you’ve finished the batch.'}

                </Alert>
              </Grid>
              <Grid item xs={6}>
                <CurrentBox
                  trackingNumber={lastBoxScanned.trackingNumber}
                  highPriority={lastBoxScanned.isRush}
                />
              </Grid>
              <Grid item xs={6}>
                <CurrentBatch
                  carrier={lastBoxScanned.carrier}
                  count={data.boxes.length}
                />
              </Grid>
              <Grid item xs={12} />
              <Grid item xs={12}>
                <BoxesScanned
                  completedShipments={data.boxes}
                  markBoxDamagedHandler={openMarkBoxDamaged}
                  deleteBoxHandler={deleteBox}
                  addBoxFunction={addBoxInTable}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid>
            <Divider orientation="vertical" />
          </Grid>
          <Grid container item spacing={1} xs={3}>
            <Grid item xs={12}>
              <SpecialCaseBoxes
                completedShipments={data.boxes}
                boxType="highpriority"
              />
              <SpecialCaseBoxes
                completedShipments={data.boxes}
                boxType="duplicate"
              />
            </Grid>
          </Grid>
        </Grid>
        {damagedBox && (
        <MarkBoxDamagedDialog
          onClose={handleDamagedClose}
          open={markDamagedDialogState.open}
          damagedBox={damagedBox}
          handleMarkDamaged={handleMarkDamaged}
        />
        )}
        <DuplicateTrackingNumberDialog
          showModal={DuplicateTrackingNumberDialogState.open}
          onClose={handleDuplicateClose}
          markOverride={handleDuplicateOverride}
          trackingNumber={trackingId}
        />
      </Box>
    </>
  );
};

export default Intake;
